import React, { useMemo, useState } from 'react';
import albertCohenPhoto from '../assets/team/albert-cohen.jpg';
import albertCohenPhotoWebp from '../assets/team/albert-cohen.webp';
import robertAportelaPhoto from '../assets/team/robert-aportela.jpg';
import robertAportelaPhotoWebp from '../assets/team/robert-aportela.webp';
import georgeVarsegiPhoto from '../assets/team/george-varsegi.jpg';
import georgeVarsegiPhotoWebp from '../assets/team/george-varsegi.webp';
import johnFortunePhoto from '../assets/team/john-fortune.jpg';
import johnFortunePhotoWebp from '../assets/team/john-fortune.webp';
import danielScharifkerPhoto from '../assets/team/daniel-scharifker.jpg';
import danielScharifkerPhotoWebp from '../assets/team/daniel-scharifker.webp';

export const teamMembers = [
  {
    name: 'Albert Cohen, MD',
    npi: '1740277128',
    // Third-party profiles of this same physician (matched on NPI, specialty and
    // location), emitted as schema.org sameAs on /team.
    profiles: [
      'https://www.doximity.com/pub/albert-cohen-md',
      'https://www.healthgrades.com/physician/dr-albert-cohen-xg3hn',
      'https://health.usnews.com/doctors/albert-cohen-189598',
    ],
    photo: albertCohenPhoto,
    photoWebp: albertCohenPhotoWebp,
    photoWidth: 254,
    photoHeight: 400,
    credentials: 'Founder and President, FCAP',
    specialties: ['GI Pathology'],
    education: {
      degree: 'Medical Degree',
      school: 'University of Miami'
    },
    residency: 'Gastrointestinal Pathology, Mount Sinai Hospital',
    boardCertification: ['Anatomic and Clinical Pathology', 'Cytopathology']
  },
  {
    name: 'Robert Aportela, MD',
    npi: '1053308593',
    profiles: [
      'https://www.doximity.com/pub/robert-aportela-md',
      'https://www.healthgrades.com/physician/dr-robert-aportela-2dxkw',
      'https://health.usnews.com/doctors/robert-aportela-188978',
    ],
    photo: robertAportelaPhoto,
    photoWebp: robertAportelaPhotoWebp,
    photoWidth: 400,
    photoHeight: 400,
    credentials: 'FCAP',
    specialties: ['GI Pathology'],
    education: {
      degree: 'Medical Degree',
      school: 'Autonomous University of Guadalajara'
    },
    residency: 'Anatomic and Clinical Pathology, Medical College of Virginia',
    boardCertification: ['Anatomic and Clinical Pathology', 'Cytopathology']
  },
  {
    name: 'George Varsegi, MD',
    npi: '1912077488',
    profiles: [
      'https://www.doximity.com/pub/george-varsegi-md',
      'https://www.healthgrades.com/physician/dr-george-varsegi-2g5c9',
      'https://health.usnews.com/doctors/george-varsegi-40304',
    ],
    photo: georgeVarsegiPhoto,
    photoWebp: georgeVarsegiPhotoWebp,
    photoWidth: 322,
    photoHeight: 400,
    credentials: 'FCAP',
    specialties: ['GI Pathology'],
    education: {
      degree: 'Medical Degree',
      school: 'Semmelweis University of Medicine'
    },
    residency: 'Anatomic and Clinical Pathology, Medical College of Wisconsin',
    boardCertification: ['Anatomic and Clinical Pathology', 'Cytopathology']
  },
  {
    name: 'John Fortune, MD',
    npi: '1881996221',
    profiles: [
      'https://www.doximity.com/pub/john-fortune-md-38d4dc22',
      'https://www.healthgrades.com/physician/dr-john-fortune-3m5kt',
      'https://health.usnews.com/doctors/john-fortune-887053',
    ],
    photo: johnFortunePhoto,
    photoWebp: johnFortunePhotoWebp,
    photoWidth: 400,
    photoHeight: 400,
    credentials: 'FCAP',
    specialties: ['Hematopathology', 'GI Pathology'],
    education: {
      degree: 'Medical Degree',
      school: 'Ross University School of Medicine'
    },
    residency: 'Anatomic and Clinical Pathology, St. Josephs Medical Center',
    boardCertification: ['Anatomic and Clinical Pathology', 'Hematopathology']
  },
  {
    name: 'Daniel Scharifker, MD',
    npi: '1376531764',
    profiles: [
      'https://www.doximity.com/pub/daniel-scharifker-md',
      'https://www.healthgrades.com/physician/dr-daniel-scharifker-2bh9d',
      'https://health.usnews.com/doctors/daniel-scharifker-193677',
    ],
    photo: danielScharifkerPhoto,
    photoWebp: danielScharifkerPhotoWebp,
    photoWidth: 322,
    photoHeight: 400,
    credentials: 'FCAP',
    specialties: ['GI Pathology'],
    education: {
      degree: 'Medical Degree',
      school: 'Central University of Venezuela'
    },
    residency: 'Anatomic and Clinical Pathology, Mount Sinai Hospital',
    boardCertification: ['Anatomic and Clinical Pathology', 'Cytopathology']
  },
];

const getInitials = (name) => {
  const nameBeforeComma = name.split(',')[0];
  return nameBeforeComma
    .split(' ')
    .map(word => word[0])
    .join('');
};

const MemberPhoto = ({ member, className, placeholderClassName }) => {
  const initials = getInitials(member.name);
  return member.photo ? (
    <picture>
      {member.photoWebp ? <source srcSet={member.photoWebp} type="image/webp" /> : null}
      <img
        className={className}
        src={member.photo}
        width={member.photoWidth}
        height={member.photoHeight}
        alt={`Portrait of ${member.name}`}
      />
    </picture>
  ) : (
    <div className={placeholderClassName}>{initials}</div>
  );
};

const MemberDetails = ({ member }) => (
  <div className="member-details">
    <div className="detail-item">
      <p className="label">Specialties</p>
      <p>{member.specialties.join(', ')}</p>
    </div>
    <div className="detail-item">
      <p className="label">Education</p>
      <p>{`${member.education.degree}, ${member.education.school}`}</p>
    </div>
    <div className="detail-item">
      <p className="label">Residency</p>
      <p>{member.residency}</p>
    </div>
    <div className="detail-item">
      <p className="label">Board Certification</p>
      <p>{member.boardCertification.join(', ')}</p>
    </div>
  </div>
);

const FounderSpotlight = ({ member }) => (
  <div className="founder-spotlight">
    <MemberPhoto
      member={member}
      className="founder-spotlight-photo"
      placeholderClassName="founder-spotlight-placeholder"
    />
    <div className="founder-spotlight-info">
      <h2>{member.name}</h2>
      <p className="member-credentials">{member.credentials}</p>
      <MemberDetails member={member} />
    </div>
  </div>
);

const TeamMember = ({ member }) => (
  <div className="team-member">
    <MemberPhoto
      member={member}
      className="team-member-photo"
      placeholderClassName="team-member-placeholder"
    />
    <div className="team-member-info">
      <h3>{member.name}</h3>
      <p className="member-credentials">{member.credentials}</p>
      {member.title ? <p className="member-title">{member.title}</p> : null}
      <MemberDetails member={member} />
    </div>
  </div>
);

function Team() {
  const [filter, setFilter] = useState('All');
  const founder = teamMembers.find((member) => member.credentials.includes('Founder'));
  const associates = teamMembers.filter((member) => member !== founder);

  const specialties = useMemo(() => {
    const set = new Set();
    associates.forEach((member) => member.specialties.forEach((item) => set.add(item)));
    return ['All', ...Array.from(set)];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visible = filter === 'All'
    ? associates
    : associates.filter((member) => member.specialties.includes(filter));

  return (
    <div>
      {founder ? <FounderSpotlight member={founder} /> : null}
      <div className="jump-nav team-filters" role="group" aria-label="Filter pathologists by specialty">
        {specialties.map((item) => (
          <button
            key={item}
            type="button"
            className={`jump-chip${filter === item ? ' is-active' : ''}`}
            onClick={() => setFilter(item)}
            aria-pressed={filter === item}
          >
            {item}
          </button>
        ))}
      </div>
      <div className="team-grid">
        {visible.map((member) => (
          <TeamMember key={member.name} member={member} />
        ))}
      </div>
    </div>
  );
}

export default Team;
